/**
 * Gym logos for owner coaches.
 *
 * Storage:   users/{uid}/gymLogos/{key}.{ext}
 * Firestore: users/{uid}/gymLogos/{logoId}
 *
 * See agents/admin_user_discussion.md for data model context.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Firestore,
  FirestoreDataConverter,
  getDocs,
  QueryDocumentSnapshot,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import type { FirebaseApp } from 'firebase/app';
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from 'firebase/storage';

import { generateStorageKey } from '../utils/ids';
import { isRecord } from '../utils/isRecord';
import type { GymLogo } from '../types/gymLogo';

export const MAX_GYM_LOGOS = 10;
export const GYM_LOGO_MAX_BYTES = 1024 * 1024; // 1 MB
export const GYM_LOGO_ALLOWED_TYPES = ['image/jpeg', 'image/png'] as const;

const USERS_COLLECTION = 'users';
const GYM_LOGOS_SUBCOLLECTION = 'gymLogos';

export function parseGymLogoDoc(id: string, data: unknown): GymLogo {
  const rec = isRecord(data) ? data : {};
  return {
    id,
    name: typeof rec.name === 'string' ? rec.name : '',
    created: rec.created instanceof Timestamp ? rec.created : Timestamp.now(),
    imgUrl: typeof rec.imgUrl === 'string' ? rec.imgUrl : '',
    storagePath: typeof rec.storagePath === 'string' ? rec.storagePath : '',
  };
}

export const gymLogoConverter: FirestoreDataConverter<GymLogo> = {
  toFirestore: ({ id: _id, ...data }) => data,
  fromFirestore: (snap: QueryDocumentSnapshot) => parseGymLogoDoc(snap.id, snap.data()),
};

/** Load all logos for this user, sorted newest first. */
export async function fetchGymLogos(db: Firestore, uid: string): Promise<GymLogo[]> {
  const snap = await getDocs(
    collection(db, USERS_COLLECTION, uid, GYM_LOGOS_SUBCOLLECTION).withConverter(gymLogoConverter),
  );
  const logos = snap.docs.map((d) => d.data());
  logos.sort((a, b) => b.created.toMillis() - a.created.toMillis());
  return logos;
}

/**
 * Upload a logo file to Storage, then persist metadata to Firestore.
 * Caller is responsible for pre-validating file type, size, and logo count.
 */
export async function uploadGymLogo(
  app: FirebaseApp,
  db: Firestore,
  uid: string,
  file: File,
  name: string,
): Promise<GymLogo> {
  const storage = getStorage(app);
  const ext = file.type === 'image/png' ? 'png' : 'jpg';
  const storagePath = `users/${uid}/gymLogos/${generateStorageKey()}.${ext}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file);
  const imgUrl = await getDownloadURL(storageRef);

  const logoData: { name: string; created: ReturnType<typeof serverTimestamp>; imgUrl: string; storagePath: string } = {
    name,
    created: serverTimestamp(),
    imgUrl,
    storagePath,
  };
  const docRef = await addDoc(
    collection(db, USERS_COLLECTION, uid, GYM_LOGOS_SUBCOLLECTION),
    logoData,
  );

  return {
    id: docRef.id,
    name,
    created: Timestamp.now(),
    imgUrl,
    storagePath,
  };
}

/** Update display name only (Firestore). */
export async function updateGymLogoName(
  db: Firestore,
  uid: string,
  logoId: string,
  name: string,
): Promise<void> {
  await updateDoc(
    doc(db, USERS_COLLECTION, uid, GYM_LOGOS_SUBCOLLECTION, logoId),
    { name },
  );
}

/** Delete logo from Storage and Firestore. */
export async function deleteGymLogo(
  app: FirebaseApp,
  db: Firestore,
  uid: string,
  logo: GymLogo,
): Promise<void> {
  const storage = getStorage(app);
  if (logo.storagePath) {
    await deleteObject(ref(storage, logo.storagePath));
  }
  await deleteDoc(
    doc(db, USERS_COLLECTION, uid, GYM_LOGOS_SUBCOLLECTION, logo.id),
  );
}
